import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Combobox } from '@/components/ui/combobox';
import { trpc } from '@/lib/trpc';
import { formatCurrency } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth-store';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';

type InvoiceType = 'PROFORMA' | 'INVOICE' | 'CREDIT_NOTE';

interface NewInvoiceItem {
  description: string;
  quantity: number;
  unitPrice: number;
  taxRate: number;
}

interface InvoiceEditFormData {
  // Bill-to details
  billToName: string;
  billToCompany: string;
  billToTaxId: string;
  billToAddress: string;
  billToEmail: string;
  billToPhone: string;
  // Invoice details
  invoiceTitle: string;
  invoiceType: InvoiceType;
  issueDate: string;
  dueDate: string;
  currency: string;
  // Notes & Terms
  notes: string;
  terms: string;
}

interface InvoiceEditModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoiceId: string;
  onSuccess?: () => void;
}

function formatDateForInput(date: Date | string | null | undefined): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';
  return d.toISOString().split('T')[0] ?? '';
}

export function InvoiceEditModal({
  open,
  onOpenChange,
  invoiceId,
  onSuccess,
}: InvoiceEditModalProps) {
  const { t } = useTranslation('bookings');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newItems, setNewItems] = useState<NewInvoiceItem[]>([]);
  const user = useAuthStore((state) => state.user);
  const utils = trpc.useUtils();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<InvoiceEditFormData>({
    defaultValues: {
      billToName: '',
      billToCompany: '',
      billToTaxId: '',
      billToAddress: '',
      billToEmail: '',
      billToPhone: '',
      invoiceTitle: '',
      invoiceType: 'INVOICE',
      issueDate: '',
      dueDate: '',
      currency: 'USD',
      notes: '',
      terms: '',
    },
  });

  const { data: invoice, isLoading: invoiceLoading } = trpc.invoice.byId.useQuery(invoiceId, {
    enabled: open && !!invoiceId,
  });

  // Pre-populate form when invoice loads
  useEffect(() => {
    if (invoice) {
      // Build customer address if bill-to address not set
      const customerAddress = [
        invoice.customer.addressLine1,
        invoice.customer.addressLine2,
        invoice.customer.city,
        invoice.customer.country,
      ]
        .filter(Boolean)
        .join(', ');

      reset({
        billToName: invoice.billToName || `${invoice.customer.firstName} ${invoice.customer.lastName}`,
        billToCompany: invoice.billToCompany || '',
        billToTaxId: invoice.billToTaxId || '',
        billToAddress: invoice.billToAddress || customerAddress,
        billToEmail: invoice.billToEmail || invoice.customer.email || '',
        billToPhone: invoice.billToPhone || invoice.customer.phone || '',
        invoiceTitle: invoice.invoiceTitle || '',
        invoiceType: invoice.invoiceType as InvoiceType,
        issueDate: formatDateForInput(invoice.issueDate),
        dueDate: formatDateForInput(invoice.dueDate),
        currency: invoice.currency,
        notes: invoice.notes || '',
        terms: invoice.terms || '',
      });
      setNewItems([]);
    }
  }, [invoice, reset]);

  const updateMutation = trpc.invoice.update.useMutation({
    onSuccess: () => {
      utils.invoice.byId.invalidate(invoiceId);
      utils.invoice.list.invalidate();
      showSuccessToast('Invoice updated successfully');
      onSuccess?.();
      onOpenChange(false);
    },
    onError: (error) => {
      setIsSubmitting(false);
      showErrorToast(error.message || 'Failed to update invoice');
    },
  });

  const addItemMutation = trpc.invoice.addItem.useMutation({
    onSuccess: () => {
      utils.invoice.byId.invalidate(invoiceId);
    },
    onError: (error) => {
      showErrorToast(error.message || 'Failed to add item');
    },
  });

  const removeItemMutation = trpc.invoice.removeItem.useMutation({
    onSuccess: () => {
      utils.invoice.byId.invalidate(invoiceId);
    },
    onError: (error) => {
      showErrorToast(error.message || 'Failed to remove item');
    },
  });

  const watchedCurrency = watch('currency');
  const watchedInvoiceType = watch('invoiceType');

  // Calculate totals for existing items
  const existingItemsSubtotal = invoice?.items.reduce(
    (sum, item) => sum + item.unitPrice * item.quantity,
    0
  ) ?? 0;
  const existingItemsTax = invoice?.items.reduce((sum, item) => sum + item.taxAmount, 0) ?? 0;

  // Calculate totals for new items
  const newItemsSubtotal = newItems.reduce(
    (sum, item) => sum + item.unitPrice * item.quantity,
    0
  );
  const newItemsTax = newItems.reduce((sum, item) => {
    const subtotal = item.unitPrice * item.quantity;
    return sum + (subtotal * item.taxRate) / 100;
  }, 0);

  // Grand totals
  const subtotal = existingItemsSubtotal + newItemsSubtotal;
  const taxTotal = existingItemsTax + newItemsTax;
  const grandTotal = subtotal + taxTotal;

  const addNewItem = () => {
    setNewItems([...newItems, { description: '', quantity: 1, unitPrice: 0, taxRate: 0 }]);
  };

  const removeNewItem = (index: number) => {
    setNewItems(newItems.filter((_, i) => i !== index));
  };

  const updateNewItem = (index: number, field: keyof NewInvoiceItem, value: string | number) => {
    const updatedItems = [...newItems];
    const item = updatedItems[index];
    if (item) {
      (item[field] as string | number) = value;
      setNewItems(updatedItems);
    }
  };

  const handleRemoveExistingItem = (itemId: string) => {
    if (confirm('Are you sure you want to remove this item?')) {
      removeItemMutation.mutate(itemId);
    }
  };

  const calculateNewItemTotal = (item: NewInvoiceItem) => {
    const itemSubtotal = item.quantity * item.unitPrice;
    const tax = (itemSubtotal * item.taxRate) / 100;
    return itemSubtotal + tax;
  };

  const onSubmit = async (data: InvoiceEditFormData) => {
    if (!user?.id) return;

    setIsSubmitting(true);

    try {
      // First, add all new items
      for (const item of newItems) {
        if (item.description.trim() && item.unitPrice > 0) {
          await addItemMutation.mutateAsync({
            invoiceId,
            description: item.description,
            quantity: item.quantity,
            unitPrice: item.unitPrice,
            taxRate: item.taxRate,
          });
        }
      }

      // Then update the invoice details
      await updateMutation.mutateAsync({
        id: invoiceId,
        billToName: data.billToName || undefined,
        billToCompany: data.billToCompany || undefined,
        billToTaxId: data.billToTaxId || undefined,
        billToAddress: data.billToAddress || undefined,
        billToEmail: data.billToEmail || undefined,
        billToPhone: data.billToPhone || undefined,
        invoiceTitle: data.invoiceTitle || undefined,
        invoiceType: data.invoiceType,
        issueDate: data.issueDate ? new Date(data.issueDate) : undefined,
        dueDate: data.dueDate ? new Date(data.dueDate) : undefined,
        currency: data.currency,
        notes: data.notes || undefined,
        terms: data.terms || undefined,
        userId: user.id,
      });
    } catch {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('invoiceEdit.title', 'Edit Invoice')}</DialogTitle>
          <DialogDescription>
            {t('invoiceEdit.description', 'Update invoice details, bill-to information, and manage line items.')}
          </DialogDescription>
        </DialogHeader>

        {invoiceLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-2 text-muted-foreground">{t('invoiceEdit.loading', 'Loading invoice...')}</span>
          </div>
        ) : invoice ? (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Bill To Details */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">{t('invoiceEdit.billTo', 'Bill To')}</h3>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="billToName">{t('invoiceEdit.name', 'Name')}</Label>
                  <Input
                    id="billToName"
                    {...register('billToName')}
                    placeholder={t('invoiceEdit.customerNamePlaceholder', 'Customer name')}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="billToCompany">{t('invoiceEdit.company', 'Company')}</Label>
                  <Input
                    id="billToCompany"
                    {...register('billToCompany')}
                    placeholder={t('invoiceEdit.companyNamePlaceholder', 'Company name')}
                  />
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="billToTaxId">{t('invoiceEdit.taxId', 'Tax ID / VAT Number')}</Label>
                  <Input
                    id="billToTaxId"
                    {...register('billToTaxId')}
                    placeholder={t('invoiceEdit.taxIdPlaceholder', 'Tax identification number')}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="billToEmail">{t('invoiceEdit.email', 'Email')}</Label>
                  <Input
                    id="billToEmail"
                    type="email"
                    {...register('billToEmail')}
                    placeholder={t('invoiceEdit.emailPlaceholder', 'billing@example.com')}
                  />
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="billToPhone">{t('invoiceEdit.phone', 'Phone')}</Label>
                  <Input
                    id="billToPhone"
                    {...register('billToPhone')}
                    placeholder={t('invoiceEdit.phonePlaceholder', 'Phone number')}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="billToAddress">{t('invoiceEdit.address', 'Address')}</Label>
                  <Input
                    id="billToAddress"
                    {...register('billToAddress')}
                    placeholder={t('invoiceEdit.addressPlaceholder', 'Full address')}
                  />
                </div>
              </div>
            </div>

            {/* Invoice Details */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">{t('invoiceEdit.invoiceDetails', 'Invoice Details')}</h3>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="invoiceTitle">{t('invoiceEdit.invoiceTitle', 'Invoice Title')}</Label>
                  <Input
                    id="invoiceTitle"
                    {...register('invoiceTitle')}
                    placeholder={t('invoiceEdit.invoiceTitlePlaceholder', 'Optional custom title')}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('invoiceEdit.invoiceType', 'Invoice Type')}</Label>
                  <Combobox
                    options={[
                      { value: 'INVOICE', label: t('invoiceEdit.types.invoice', 'Tax Invoice') },
                      { value: 'PROFORMA', label: t('invoiceEdit.types.proforma', 'Proforma Invoice') },
                      { value: 'CREDIT_NOTE', label: t('invoiceEdit.types.creditNote', 'Credit Note') },
                    ]}
                    value={watchedInvoiceType}
                    onValueChange={(value) => setValue('invoiceType', value as InvoiceType)}
                    placeholder={t('invoiceEdit.selectType', 'Select type')}
                    searchPlaceholder={t('common.search', 'Search...')}
                  />
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="issueDate">{t('invoiceEdit.issueDate', 'Issue Date')}</Label>
                  <Input
                    id="issueDate"
                    type="date"
                    {...register('issueDate')}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="dueDate">{t('invoiceEdit.dueDate', 'Due Date')}</Label>
                  <Input
                    id="dueDate"
                    type="date"
                    {...register('dueDate', { required: t('invoiceEdit.dueDateRequired', 'Due date is required') })}
                  />
                  {errors.dueDate && (
                    <p className="text-sm text-red-500">{errors.dueDate.message}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>{t('invoiceEdit.currency', 'Currency')}</Label>
                  <Combobox
                    options={[
                      { value: 'USD', label: 'USD - US Dollar' },
                      { value: 'EUR', label: 'EUR - Euro' },
                      { value: 'GBP', label: 'GBP - British Pound' },
                      { value: 'TRY', label: 'TRY - Turkish Lira' },
                    ]}
                    value={watchedCurrency}
                    onValueChange={(value) => setValue('currency', value)}
                    placeholder={t('invoiceEdit.selectCurrency', 'Select currency')}
                    searchPlaceholder={t('invoiceEdit.searchCurrencies', 'Search currencies...')}
                  />
                </div>
              </div>
            </div>

            {/* Existing Line Items */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">{t('invoiceEdit.lineItems', 'Line Items')}</h3>
                <Button type="button" variant="outline" size="sm" onClick={addNewItem}>
                  <Plus className="mr-1 h-4 w-4" />
                  {t('invoiceEdit.addItem', 'Add Item')}
                </Button>
              </div>

              {/* Existing Items (Read-only, can only be removed) */}
              {invoice.items.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">{t('invoiceEdit.existingItems', 'Existing Items')}</p>
                  {invoice.items.map((item) => (
                    <div
                      key={item.id}
                      className="grid gap-3 rounded-lg border bg-muted/30 p-3 sm:grid-cols-12"
                    >
                      <div className="sm:col-span-5">
                        <Label className="text-xs text-muted-foreground">{t('invoiceEdit.description', 'Description')}</Label>
                        <Input value={item.description} disabled className="bg-muted" />
                      </div>
                      <div className="sm:col-span-2">
                        <Label className="text-xs text-muted-foreground">{t('invoiceEdit.qty', 'Qty')}</Label>
                        <Input value={item.quantity} disabled className="bg-muted" />
                      </div>
                      <div className="sm:col-span-2">
                        <Label className="text-xs text-muted-foreground">{t('invoiceEdit.unitPrice', 'Unit Price')}</Label>
                        <Input
                          value={formatCurrency(item.unitPrice, watchedCurrency)}
                          disabled
                          className="bg-muted"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <Label className="text-xs text-muted-foreground">{t('invoiceEdit.taxPercent', 'Tax %')}</Label>
                        <Input value={`${item.taxRate}%`} disabled className="bg-muted" />
                      </div>
                      <div className="flex items-end sm:col-span-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveExistingItem(item.id)}
                          disabled={removeItemMutation.isPending}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                      <div className="text-right text-sm font-medium sm:col-span-12">
                        {t('invoiceEdit.itemTotal', 'Item Total')}: {formatCurrency(item.totalAmount, watchedCurrency)}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* New Items (Editable) */}
              {newItems.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">{t('invoiceEdit.newItems', 'New Items')}</p>
                  {newItems.map((item, index) => (
                    <div
                      key={index}
                      className="grid gap-3 rounded-lg border border-primary/50 p-3 sm:grid-cols-12"
                    >
                      <div className="sm:col-span-5">
                        <Label className="text-xs text-muted-foreground">{t('invoiceEdit.description', 'Description')}</Label>
                        <Input
                          value={item.description}
                          onChange={(e) => updateNewItem(index, 'description', e.target.value)}
                          placeholder={t('invoiceEdit.itemDescriptionPlaceholder', 'Item description')}
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <Label className="text-xs text-muted-foreground">{t('invoiceEdit.qty', 'Qty')}</Label>
                        <Input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => updateNewItem(index, 'quantity', Number(e.target.value))}
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <Label className="text-xs text-muted-foreground">{t('invoiceEdit.unitPrice', 'Unit Price')}</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.unitPrice}
                          onChange={(e) => updateNewItem(index, 'unitPrice', Number(e.target.value))}
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <Label className="text-xs text-muted-foreground">{t('invoiceEdit.taxPercent', 'Tax %')}</Label>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          value={item.taxRate}
                          onChange={(e) => updateNewItem(index, 'taxRate', Number(e.target.value))}
                        />
                      </div>
                      <div className="flex items-end sm:col-span-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeNewItem(index)}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                      <div className="text-right text-sm font-medium sm:col-span-12">
                        {t('invoiceEdit.itemTotal', 'Item Total')}: {formatCurrency(calculateNewItemTotal(item), watchedCurrency)}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {invoice.items.length === 0 && newItems.length === 0 && (
                <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-8 text-center">
                  <p className="text-sm text-muted-foreground">{t('invoiceEdit.noLineItems', 'No line items')}</p>
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    onClick={addNewItem}
                    className="mt-2"
                  >
                    {t('invoiceEdit.addFirstItem', 'Add your first item')}
                  </Button>
                </div>
              )}
            </div>

            {/* Totals Summary */}
            <div className="flex justify-end">
              <div className="w-72 space-y-2 rounded-lg bg-muted p-4">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t('invoiceEdit.subtotal', 'Subtotal')}</span>
                  <span>{formatCurrency(subtotal, watchedCurrency)}</span>
                </div>
                {taxTotal > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{t('invoiceEdit.tax', 'Tax')}</span>
                    <span>{formatCurrency(taxTotal, watchedCurrency)}</span>
                  </div>
                )}
                <div className="flex justify-between border-t pt-2 text-lg font-bold">
                  <span>{t('invoiceEdit.total', 'Total')}</span>
                  <span>{formatCurrency(grandTotal, watchedCurrency)}</span>
                </div>
                {(invoice.amountPaid ?? 0) > 0 && (
                  <>
                    <div className="flex justify-between text-sm text-green-600">
                      <span>{t('invoiceEdit.paid', 'Paid')}</span>
                      <span>-{formatCurrency(invoice.amountPaid, watchedCurrency)}</span>
                    </div>
                    <div className="flex justify-between border-t pt-2 font-semibold">
                      <span>{t('invoiceEdit.balanceDue', 'Balance Due')}</span>
                      <span className="text-orange-600">
                        {formatCurrency(grandTotal - invoice.amountPaid, watchedCurrency)}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Notes & Terms */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">{t('invoiceEdit.notesAndTerms', 'Notes & Terms')}</h3>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="notes">{t('invoiceEdit.notes', 'Notes')}</Label>
                  <Textarea
                    id="notes"
                    {...register('notes')}
                    placeholder={t('invoiceEdit.notesPlaceholder', 'Additional notes for the customer...')}
                    rows={4}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="terms">{t('invoiceEdit.paymentTerms', 'Payment Terms')}</Label>
                  <Textarea
                    id="terms"
                    {...register('terms')}
                    placeholder={t('invoiceEdit.termsPlaceholder', 'Payment terms and conditions...')}
                    rows={4}
                  />
                </div>
              </div>
            </div>

            {/* Error Messages */}
            {updateMutation.error && (
              <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
                {updateMutation.error.message}
              </div>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t('invoiceEdit.saveChanges', 'Save Changes')}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <div className="py-8 text-center text-muted-foreground">
            {t('invoiceEdit.notFound', 'Invoice not found')}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
